import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  revenueService,
  CreateFinancialPeriodData,
  CreateRevenueStreamData,
  CreateRevenueTargetData,
} from '../services';

interface PeriodFilters {
  page?: number;
  per_page?: number;
  search?: string;
}

// Financial Periods
export const useFinancialPeriods = (filters?: PeriodFilters) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['financialPeriods', filters],
    queryFn: async () => {
      const response = await revenueService.getFinancialPeriods(filters);
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateFinancialPeriodData) => revenueService.createFinancialPeriod(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financialPeriods'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateFinancialPeriodData> }) =>
      revenueService.updateFinancialPeriod(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financialPeriods'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => revenueService.deleteFinancialPeriod(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financialPeriods'] });
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: number) => revenueService.activateFinancialPeriod(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financialPeriods'] });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => revenueService.deactivateFinancialPeriod(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financialPeriods'] });
    },
  });

  return {
    periods: data,
    isLoading,
    error,
    createPeriod: createMutation.mutateAsync,
    updatePeriod: updateMutation.mutateAsync,
    deletePeriod: deleteMutation.mutateAsync,
    activatePeriod: activateMutation.mutateAsync,
    deactivatePeriod: deactivateMutation.mutateAsync,
  };
};

export const useActivePeriod = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['financialPeriods', 'active'],
    queryFn: async () => {
      const response = await revenueService.getActivePeriod();
      return response.data;
    },
  });

  return {
    activePeriod: data,
    isLoading,
    error,
  };
};

// Revenue Streams
export const useRevenueStreams = (periodId?: number, page?: number) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['revenueStreams', periodId, page],
    queryFn: async () => {
      const response = await revenueService.getRevenueStreams(periodId, page);
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateRevenueStreamData) => revenueService.createRevenueStream(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revenueStreams'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateRevenueStreamData> }) =>
      revenueService.updateRevenueStream(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revenueStreams'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => revenueService.deleteRevenueStream(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revenueStreams'] });
    },
  });

  const assignServicesMutation = useMutation({
    mutationFn: ({ streamId, serviceIds }: { streamId: number; serviceIds: number[] }) =>
      revenueService.assignServices(streamId, serviceIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revenueStreams'] });
    },
  });

  const removeServiceMutation = useMutation({
    mutationFn: ({ streamId, serviceId }: { streamId: number; serviceId: number }) =>
      revenueService.removeService(streamId, serviceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revenueStreams'] });
    },
  });

  return {
    streams: data,
    isLoading,
    error,
    createStream: createMutation.mutateAsync,
    updateStream: updateMutation.mutateAsync,
    deleteStream: deleteMutation.mutateAsync,
    assignServices: assignServicesMutation.mutateAsync,
    removeService: removeServiceMutation.mutateAsync,
  };
};

export const useRevenueStreamSummary = (streamId: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['revenueStreams', streamId, 'summary'],
    queryFn: async () => {
      const response = await revenueService.getStreamSummary(streamId);
      return response.data;
    },
    enabled: !!streamId,
  });

  return {
    summary: data,
    isLoading,
    error,
  };
};

// Revenue Targets
export const useRevenueTargets = (streamId?: number, page?: number) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['revenueTargets', streamId, page],
    queryFn: async () => {
      const response = await revenueService.getRevenueTargets(streamId, page);
      return response.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateRevenueTargetData) => revenueService.createRevenueTarget(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revenueTargets'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateRevenueTargetData> }) =>
      revenueService.updateRevenueTarget(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revenueTargets'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => revenueService.deleteRevenueTarget(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['revenueTargets'] });
    },
  });

  return {
    targets: data,
    isLoading,
    error,
    createTarget: createMutation.mutateAsync,
    updateTarget: updateMutation.mutateAsync,
    deleteTarget: deleteMutation.mutateAsync,
  };
};

export const useTargetsByLocation = (
  streamId: number,
  locationType: 'subcounty' | 'ward' | 'zone',
  locationId: number
) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['revenueTargets', streamId, locationType, locationId],
    queryFn: async () => {
      const response = await revenueService.getTargetsByLocation(streamId, locationType, locationId);
      return response.data;
    },
    enabled: !!streamId && !!locationId,
  });

  return {
    targets: data,
    isLoading,
    error,
  };
};

// Finance Dashboard Overview
export const useFinanceOverview = (periodId?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['financeOverview', periodId],
    queryFn: async () => {
      const response = await revenueService.getFinanceOverview(periodId);
      return response.data;
    },
  });

  return {
    overview: data,
    isLoading,
    error,
  };
};

// Finance Reports
export const useFinanceReports = (periodId?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['financeReports', periodId],
    queryFn: async () => {
      const response = await revenueService.getFinanceReports(periodId);
      return response.data;
    },
  });

  return {
    reports: data,
    isLoading,
    error,
  };
};
